import React from 'react';
import moment from 'moment';
import {
  AlarmEventType,
  StopTravelStatus,
  SystemState,
  VehicleScheduleState,
  VehicleState,
} from '../../redux/type';
import {
  stringToEnum,
  translateMileageNum,
  convertSpeedMsToKmh,
} from '../../utils/util';
import posIcon from '../../assets/img/views/common/pos_icon.png';
import { Tooltip } from 'antd';
import { PermissionTab } from '../../utils/constant';
import { isEmpty } from 'lodash';

export const tabListPermission = new Map([
  [PermissionTab.SPV_TRACK_HISTORY, '全程跟踪'],
  [PermissionTab.SPV_EMERGENCY_STOP, '急刹分析'],
  [PermissionTab.SPV_DATA_ANALYSIS, '数据分析'],
  [PermissionTab.SPV_ACCIDENT_ANALYSIS, '事故分析'],
]);

export const formatRealTimeDataStr = (data: any) => {
  const {
    systemState,
    vehicleState,
    taskType,
    scheduleState,
    roverVersion,
    androidVersion,
    videoVersion,
    mapVersion,
    lon,
    lat,
  } = data;

  const fieldStr: {
    label: string;
    value: string | number | undefined | null;
    key: string;
  }[] = [
    {
      label: '系统状态',
      key: 'systemState',
      value: stringToEnum(SystemState, systemState),
    },
    {
      label: '车辆模式',
      key: 'vehicleState',
      value: stringToEnum(VehicleState, vehicleState),
    },
    {
      label: '业务阶段',
      key: 'taskType',
      value: taskType,
    },
    {
      label: '配送状态',
      key: 'scheduleState',
      value: stringToEnum(VehicleScheduleState, scheduleState),
    },
    {
      label: 'rover版本',
      key: 'roverVersion',
      value: roverVersion,
    },
    {
      label: '安卓版本',
      key: 'androidVersion',
      value: androidVersion,
    },
    {
      label: '视频版本',
      key: 'videoVersion',
      value: videoVersion,
    },
    {
      label: '地图版本',
      key: 'mapVersion',
      value: mapVersion,
    },
    {
      label: '实时坐标经纬度',
      key: 'lonLat',
      value: `${lon}、${lat}`,
    },
  ];
  return fieldStr.reduce((_accumulator: any, _currentValue) => {
    return _accumulator.concat(
      `${_currentValue.label}：${_currentValue.value || '--'}；`,
    );
  }, '');
};

export const getFieldsGroupData = (resData: any) => {
  const {
    alarmList,
    androidVersion,
    lat,
    lon,
    mapVersion,
    power,
    roverVersion,
    scheduleState,
    speed,
    systemState,
    vehicleState,
    videoVersion,
    taskType,
    finishedMileage,
    totalMileage,
    heading,
    inverseAddress,
    stopName,
    stopTravelStatus,
    roverCloudVersion,
    androidCloudVersion,
    videoCloudVersion,
    currentScheduleFinishedMileage,
  } = resData || {};
  const fieldsGroup: any[] = [
    {
      title: '车辆状态',
      fields: [
        {
          label: '报警事件',
          key: 'alarmList',
          value: alarmList
            ?.map((item: AlarmEventType) => stringToEnum(AlarmEventType, item))
            .join(','),
        },
        {
          label: '系统状态',
          key: 'systemState',
          value: stringToEnum(SystemState, systemState),
        },
        {
          label: '车辆模式',
          key: 'vehicleState',
          value: stringToEnum(VehicleState, vehicleState),
        },
      ],
    },
    {
      title: '业务状态',
      fields: [
        {
          label: '业务阶段',
          key: 'taskType',
          value: taskType,
        },
        {
          label: '配送状态',
          key: 'scheduleState',
          value: stringToEnum(VehicleScheduleState, scheduleState),
        },
        {
          label: '里程信息',
          key: 'mileage',
          value: `${translateMileageNum(finishedMileage)}/${translateMileageNum(
            totalMileage,
          )} km`,
        },
        {
          label:
            stopTravelStatus === StopTravelStatus.STAY
              ? '当前停靠点'
              : '下一停靠点',
          key: 'stopName',
          value: stopName,
        },
      ],
    },
    {
      title: '行驶信息',
      fields: [
        {
          label: '剩余电量',
          key: 'power',
          value: power?.toFixed(0),
          unit: '%',
        },
        {
          label: '速度',
          key: 'speed',
          value: convertSpeedMsToKmh(speed, 1),
        },
        {
          label: '经度位置',
          key: 'lon',
          value: lon,
        },
        {
          label: '纬度位置',
          key: 'lat',
          value: lat,
        },
        {
          label: '逆地址',
          value: inverseAddress,
          key: 'inverseAddress',
          icon: inverseAddress ? <img src={posIcon} /> : null,
        },
        {
          label: '朝向',
          key: 'heading',
          value: heading,
        },
      ],
    },
    {
      title: '版本',
      fields: [
        {
          label: 'Rover版本',
          key: 'roverVersion',
          value: roverVersion,
          icon: roverCloudVersion && roverCloudVersion !== roverVersion && (
            <Tooltip title={`待升级版本${roverCloudVersion}`}>
              <i className='tip-info'>!</i>
            </Tooltip>
          ),
        },
        {
          label: '安卓版本',
          key: 'androidVersion',
          value: androidVersion,
          icon: androidCloudVersion &&
            androidVersion !== androidCloudVersion && (
              <Tooltip title={`待升级版本${androidCloudVersion}`}>
                <i className='tip-info'>!</i>
              </Tooltip>
            ),
        },
        {
          label: '视频版本',
          key: 'videoVersion',
          value: videoVersion,
          icon: videoCloudVersion && videoCloudVersion !== videoVersion && (
            <Tooltip title={`待升级版本${videoCloudVersion}`}>
              <i className='tip-info'>!</i>
            </Tooltip>
          ),
        },
        {
          label: '地图版本',
          key: 'mapVersion',
          value: mapVersion,
        },
      ],
    },
  ];

  return fieldsGroup;
};

export const columns: any = [
  {
    title: '序号',
    dataIndex: 'orderIndex',
    align: 'left',
    key: 'orderIndex',
    width: 100,
  },
  {
    title: '指令名称',
    dataIndex: 'eventTypeName',
    align: 'left',
    key: 'eventTypeName',
  },
  {
    title: '指令内容',
    dataIndex: 'content',
    align: 'left',
    key: 'content',
    width: 400,
  },
  {
    title: '发生时间',
    dataIndex: 'reportTime',
    key: 'reportTime',
    align: 'left',
    render: (text: string) => {
      return moment.utc(text).format('YYYY-MM-DD HH:mm:ss');
    },
  },
  {
    title: '城市',
    dataIndex: 'cityName',
    align: 'left',
    key: 'cityName',
  },
  {
    title: '站点',
    dataIndex: 'stationName',
    align: 'left',
    key: 'stationName',
  },
  {
    title: '车号',
    dataIndex: 'vehicleName',
    align: 'left',
    key: 'vehicleName',
  },
  {
    title: '来源系统',
    dataIndex: 'sourceName',
    align: 'left',
    key: 'sourceName',
  },
  {
    title: '指令负责人',
    dataIndex: 'maintainerUser',
    key: 'maintainerUser',
    align: 'left',
    render: (text: any) => <a>{text}</a>,
  },
  {
    title: '指令接收人',
    dataIndex: 'followUser',
    key: 'followUser',
    align: 'left',
    render: (text: any) => <a>{text}</a>,
  },
  {
    title: 'jira号',
    dataIndex: 'jiraNo',
    key: 'jiraNo',
    align: 'left',
    render: (text: any) => <a>{text}</a>,
  },
  {
    title: '操作',
    align: 'left',
    dataIndex: 'operate',
    key: 'operate',
    width: 240,
  },
];

export const scheduleColunms: any[] = [
  {
    title: '车号',
    align: 'left',
    dataIndex: 'vehicleName',
    key: 'vehicleName',
  },
  {
    title: '调度ID',
    align: 'left',
    dataIndex: 'scheduleId',
    key: 'scheduleId',
    width: 240,
  },
  {
    title: '开始时间',
    align: 'left',
    dataIndex: 'startTime',
    key: 'startTime',
  },
  {
    title: '结束时间',
    align: 'left',
    dataIndex: 'endTime',
    key: 'endTime',
  },
  {
    title: '路线',
    align: 'left',
    dataIndex: 'route',
    key: 'route',
  },
];

export const HighPriorityEventMap = new Map([
  ['VEHICLE_CRASH', 'VEHICLE_CRASH'],
]);

export const isStacked = (key: string, stackGroup: any) => {
  if (isEmpty(stackGroup)) {
    return false;
  }
  stackGroup.forEach((k: string) => {
    return stackGroup[k].includes(key);
  });
};
